import type React from "react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import type { MessageStatusChangeRequestV2 } from "@/api/types";
import { Form } from "@/components/Form/Form";
import { FormTagSelect } from "@/components/Form/FormTagSelect";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { communityFeedMutations } from "@/queries/community-feed/mutations";
import { useTranslation } from "@/translations";

const FORM_ID = "community-message-close-post-sold-modal-form";

type CommunityMessageClosePostSoldReasonModalProps = ModalBaseProps & {
  messageId: string;
};

type CommunityMessageClosePostSoldReasonModalFormValues = {
  reason: Extract<
    MessageStatusChangeRequestV2["reason"],
    "marketplaceSoldInsideCommunity" | "marketplaceSoldOutsideCommunity"
  >[];
};

export function CommunityMessageClosePostSoldReasonModal({
  messageId,
  isOpened,
  onOpenChange,
}: CommunityMessageClosePostSoldReasonModalProps): React.ReactNode {
  const { t } = useTranslation();
  const form = useForm<CommunityMessageClosePostSoldReasonModalFormValues>({
    defaultValues: {
      reason: [],
    },
  });

  useEffect(() => {
    form.reset();
  }, [isOpened, form]);

  const updateMessageStatus = communityFeedMutations.useUpdateMessageStatus();

  const handleSubmit = async (data: CommunityMessageClosePostSoldReasonModalFormValues) => {
    if (data.reason.length === 0) return;

    await updateMessageStatus.mutateAsync({
      messageId,
      data: {
        newStatus: "deleted",
        reason: data.reason[0],
      },
    });

    onOpenChange(false);
  };

  return (
    <Modal.Root title={t("component.community-post.close-post-confirm-modal.title")} {...{ isOpened, onOpenChange }}>
      <Modal.Body>
        <Form.Root id={FORM_ID} formMethods={form} onSubmit={handleSubmit}>
          <Form.Body>
            <FormField
              label={t("component.community-post.close-post-sold-modal.reason-field.label")}
              description={t("component.community-post.close-post-sold-modal.reason-field.description")}
              required
            >
              <FormTagSelect<CommunityMessageClosePostSoldReasonModalFormValues, "reason">
                name="reason"
                data-testid="closing-post-sold-modal-options-container"
                items={[
                  {
                    label: t("component.community-post.close-post-sold-modal.reason-field.option.inside-community"),
                    value: "marketplaceSoldInsideCommunity",
                  },
                  {
                    label: t("component.community-post.close-post-sold-modal.reason-field.option.outside-community"),
                    value: "marketplaceSoldOutsideCommunity",
                  },
                ]}
                rules={{
                  validate: {
                    required: (value) => {
                      if (value.length === 0) {
                        return t("component.community-post.close-post-sold-modal.reason-field.error");
                      }
                    },
                  },
                }}
              />
            </FormField>
          </Form.Body>
        </Form.Root>
      </Modal.Body>

      <Modal.Actions>
        <Button
          data-testid="modal-confirm-delete"
          tone="error"
          form={FORM_ID}
          type="submit"
          isLoading={updateMessageStatus.isPending}
        >
          {t("component.community-post.close-post-confirm-modal.btn.delete-post")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
